import {
  faCog,
  faPlus,
  faTimes,
  faTrashAlt,
} from "@fortawesome/pro-regular-svg-icons";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { twJoin } from "tailwind-merge";

import {
  ConfigSection,
  OutputMode,
  SchemaTab,
  useAgentVersionConfigPanel,
} from "src/agents/AgentVersionPage/AgentVersionConfigPanelContext";
import { JsonSchemaEditorConfig } from "src/agents/AgentVersionPage/JsonSchemaDrawer";
import { useSideDrawer } from "src/agents/base/SideDrawer";
import { AgentVersionConfigurationInput } from "src/agents/types";
import { PropertyValue, Schema } from "src/api/flowTypes";
import { Button } from "src/base-components/Button";
import { ErrorHint } from "src/base-components/ErrorHint";
import { FixedSimpleDropdown } from "src/base-components/FixedSimpleDropdown";
import { Icon } from "src/base-components/Icon";
import { Input } from "src/base-components/Input";
import { Pill } from "src/base-components/Pill";
import { PillToggle } from "src/base-components/PillToggle";
import { Switch } from "src/base-components/Switch";
import { Tabs } from "src/base-components/Tabs";
import { SCHEMA_TYPE_ICONS } from "src/base-components/TypeIcons";
import { JsonSchemaEmptyState } from "src/jsonSchema/editor/JsonSchemaEmptyState";
import {
  isArrayOfPrimitive,
  isObject,
  isObjectWithProperties,
  isPrimitive,
  isUnionOfPrimitivesOnly,
  JsonSchema,
  JsonSchemaNode,
} from "src/jsonSchema/types";
import { JSONSchemaRenderer } from "src/jsonSchema/viewer";
import { createTitleOnlyHeaderRenderer } from "src/jsonSchema/viewer/JSONSchemaRenderer";
import { SchemaOptions } from "src/router/SearchParams";
import {
  EnumValueObject,
  PropertyConverter,
  PropertyUI,
  SchemaConverter,
  SchemaUI,
} from "src/schema/schemaMappingUtils";
import { deepCopyWithoutKeys } from "src/utils/deepCopyWithoutKeys";
import { useOpenOnHover } from "src/utils/useOpenOnHover";
import { useScrollIntoView } from "src/utils/useScrollIntoView";

// ============================================================================
// Types
// ============================================================================

// Agent-specific property UI with description
export type AgentSchemaPropertyUI = PropertyUI & {
  description?: string;
};

// Agent-specific schema UI
export type AgentSchemaUI = SchemaUI & {
  properties: AgentSchemaPropertyUI[];
};

// Backend schema with description
type AgentSchemaBE = Schema & {
  properties: Record<string, PropertyValue & { description?: string }>;
};

// ============================================================================
// Type Options
// ============================================================================

const AGENT_SCHEMA_TYPE_OPTIONS = [
  { key: "string", value: "string", icon: SCHEMA_TYPE_ICONS.string },
  { key: "number", value: "number", icon: SCHEMA_TYPE_ICONS.number },
  { key: "boolean", value: "boolean", icon: SCHEMA_TYPE_ICONS.boolean },
  { key: "integer", value: "integer", icon: SCHEMA_TYPE_ICONS.integer },
  { key: "date", value: "date", icon: SCHEMA_TYPE_ICONS.date },
  { key: "datetime", value: "datetime", icon: SCHEMA_TYPE_ICONS.datetime },
  { key: "array", value: "array", icon: SCHEMA_TYPE_ICONS.array },
  { key: "object", value: "object", icon: SCHEMA_TYPE_ICONS.object },
  { key: "enum", value: "enum", icon: SCHEMA_TYPE_ICONS.enum },
];

// ============================================================================
// Conversion Functions
// ============================================================================

const DEFAULT_SCHEMA: AgentSchemaBE = {
  $schema: "https://json-schema.org/draft/2020-12/schema",
  type: "object",
  properties: {},
  required: [],
  order: [],
  sensitive: [],
};

const EMPTY_UI_SCHEMA: AgentSchemaUI = {
  $schema: DEFAULT_SCHEMA.$schema,
  type: "object" as const,
  properties: [],
};

// Convert backend JSON schema to UI-friendly format
export const convertAgentSchemaBEtoUI = (
  schemaBE: AgentSchemaBE | Schema | null | undefined,
): AgentSchemaUI | null => {
  if (!schemaBE || !schemaBE.properties) return null;

  // Use the shared SchemaConverter for the heavy lifting
  const schemaUI = SchemaConverter.beToUI(
    schemaBE as Schema,
    SchemaOptions.Output,
  );

  // Add agent-specific fields (description)
  return {
    ...schemaUI,
    properties: schemaUI.properties.map((property) => ({
      ...property,
      description:
        (
          schemaBE.properties as Record<
            string,
            PropertyValue & { description?: string }
          >
        )[property.fieldName]?.description ?? "",
    })),
  };
};

// Convert UI format back to backend JSON schema
export const convertAgentSchemaUIToBE = (
  schemaUI: AgentSchemaUI,
): AgentSchemaBE => {
  // Use the shared SchemaConverter for the heavy lifting
  const schemaBE = SchemaConverter.uiToBE(schemaUI, SchemaOptions.Output);

  // Create mapping for agent-specific fields
  const descriptionMapping = Object.fromEntries(
    schemaUI.properties.map((property) => [
      property.fieldName,
      property.description,
    ]),
  );

  // Add description back to each property
  return {
    ...schemaBE,
    properties: Object.fromEntries(
      Object.entries(schemaBE.properties).map(([fieldName, property]) => [
        fieldName,
        {
          ...property,
          description: descriptionMapping[fieldName],
        },
      ]),
    ),
  };
};

// Factory for new empty properties
export const getAgentNewProperty = (
  existingProperties: AgentSchemaPropertyUI[],
): AgentSchemaPropertyUI => {
  // Generate unique field name
  const existingNames = new Set(existingProperties.map((p) => p.fieldName));
  let counter = existingProperties.length + 1;
  while (existingNames.has(`field_${counter}`)) {
    counter++;
  }

  return {
    fieldName: `field_${counter}`,
    type: ["string", false], // PropertyTypeUI format: [type, nullable]
    required: true,
    sensitive: false,
    description: "",
  };
};

// ============================================================================
// Main Component: AgentSchemaEditor
// ============================================================================

type AgentSchemaEditorProps = {
  immutable: boolean;
  onOpenJsonEditor: (config: JsonSchemaEditorConfig) => void;
};

export const AgentSchemaEditor: React.FC<AgentSchemaEditorProps> = ({
  immutable,
  onOpenJsonEditor,
}) => {
  const { schemaTab, setSchemaTab, focusSchemaSection, registerScrollFn } =
    useAgentVersionConfigPanel();
  const { control } = useFormContext<AgentVersionConfigurationInput>();

  // Scroll hook for tabs - also used as the Schema section scroll target
  const { ref: tabsRef, scrollIntoView: scrollTabs } = useScrollIntoView();

  // Register tabs scroll as the Schema section scroll target
  // This ensures both accordion expand and pill clicks scroll to the same position
  useEffect(() => {
    registerScrollFn(ConfigSection.Schema, scrollTabs);
  }, [registerScrollFn, scrollTabs]);

  return (
    <div ref={tabsRef}>
      <p className="mb-4 text-gray-500 font-inter-normal-12px">
        Configure the input and output fields for this Agent version
      </p>

      <div className="rounded-lg bg-gray-50 px-4 py-3">
        <Tabs
          panelsClassName="pt-5"
          selectedKey={schemaTab}
          tabListClassName="border-b border-gray-200"
          tabs={[
            {
              key: "input",
              label: "Input",
              content: (
                <Controller
                  control={control}
                  name="input_schema"
                  render={({ field }) => (
                    <InputSchemaEditor
                      immutable={immutable}
                      initialValue={field.value as Schema}
                      onChange={field.onChange}
                    />
                  )}
                />
              ),
            },
            {
              key: "output",
              label: "Output",
              content: (
                <Controller
                  control={control}
                  name="output_schema"
                  render={({ field }) => (
                    <OutputSchemaEditor
                      focusSchemaSection={focusSchemaSection}
                      immutable={immutable}
                      initialValue={field.value as Schema | null}
                      onChange={field.onChange}
                      onOpenJsonEditor={onOpenJsonEditor}
                    />
                  )}
                />
              ),
            },
          ]}
          unmountPanels={false}
          onChange={(key) => {
            setSchemaTab(key as SchemaTab);
            scrollTabs();
          }}
        />
      </div>
    </div>
  );
};

// ============================================================================
// Schema Properties Editor
// ============================================================================

type SchemaPropertiesFormValues = {
  properties: AgentSchemaPropertyUI[];
};

type SchemaPropertiesEditorProps = {
  initialProperties: AgentSchemaPropertyUI[];
  immutable: boolean;
  onChange: (properties: AgentSchemaPropertyUI[]) => void;
  dataLocPrefix: "input" | "output";
};

const SchemaPropertiesEditor: React.FC<SchemaPropertiesEditorProps> = ({
  initialProperties,
  immutable,
  onChange,
  dataLocPrefix,
}) => {
  const bottomRef = useRef<HTMLDivElement>(null);
  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }, 50);
  }, []);

  // Track when change originated from form's own onChange (to skip unnecessary resets)
  const isInternalChangeRef = useRef(false);

  const formMethods = useForm<SchemaPropertiesFormValues>({
    defaultValues: {
      properties: initialProperties,
    },
    mode: "onChange",
  });

  const { fields, append, remove } = useFieldArray({
    control: formMethods.control,
    name: "properties",
  });

  // Reset form when initialProperties changes externally (e.g., JSON editor saves)
  useEffect(() => {
    if (isInternalChangeRef.current) {
      isInternalChangeRef.current = false;
      return; // Skip reset - change came from form itself
    }
    formMethods.reset({ properties: initialProperties });
  }, [initialProperties, formMethods]);

  const handleAddField = useCallback(() => {
    const properties = formMethods.getValues("properties");
    const newProperty = getAgentNewProperty(properties);
    append(newProperty);
    scrollToBottom();
  }, [append, formMethods, scrollToBottom]);

  // Subscribe to form changes and propagate valid changes to parent
  useEffect(
    () =>
      formMethods.subscribe({
        formState: { values: true },
        callback: async ({ values: _ }) => {
          const isValid = await formMethods.trigger();
          if (!isValid) return;

          const currentValues = formMethods.getValues();
          isInternalChangeRef.current = true; // Mark as internal change
          onChange(currentValues.properties);
        },
      }),
    [formMethods, onChange],
  );

  return (
    <FormProvider {...formMethods}>
      {fields.length > 0 && <SchemaFieldHeader />}
      {fields.map((field, index) => (
        <SchemaFieldRow
          key={field.id}
          immutable={immutable}
          index={index}
          onDelete={() => remove(index)}
        />
      ))}
      <Button
        dataLoc={`add-${dataLocPrefix}-schema-field`}
        disabled={immutable}
        iconLeft={faPlus}
        size="sm"
        variant="secondary"
        onClick={handleAddField}
      >
        Add field
      </Button>
      <div ref={bottomRef} />
    </FormProvider>
  );
};

// ============================================================================
// Input Schema Editor
// ============================================================================

type InputSchemaEditorProps = {
  initialValue: Schema | null;
  immutable: boolean;
  onChange: (jsonSchema: Schema | null) => void;
};

const InputSchemaEditor: React.FC<InputSchemaEditorProps> = ({
  initialValue,
  immutable,
  onChange,
}) => {
  const initialProperties = useMemo(
    () => convertAgentSchemaBEtoUI(initialValue)?.properties ?? [],
    [initialValue],
  );

  const handleChange = useCallback(
    (properties: AgentSchemaPropertyUI[]) => {
      if (properties.length === 0) {
        onChange(null);
        return;
      }
      const backendSchema = convertAgentSchemaUIToBE({
        ...DEFAULT_SCHEMA,
        properties,
      } as AgentSchemaUI);
      onChange(backendSchema);
    },
    [onChange],
  );

  return (
    <SchemaPropertiesEditor
      dataLocPrefix="input"
      immutable={immutable}
      initialProperties={initialProperties}
      onChange={handleChange}
    />
  );
};

// ============================================================================
// Output Schema Editor
// ============================================================================

type FocusSchemaSectionOptions = {
  tab?: SchemaTab;
  outputMode?: OutputMode;
  scrollDelay?: number;
};

type OutputSchemaEditorProps = {
  initialValue: Schema | null;
  immutable: boolean;
  onChange: (jsonSchema: Schema | null) => void;
  onOpenJsonEditor: (config: JsonSchemaEditorConfig) => void;
  focusSchemaSection: (options?: FocusSchemaSectionOptions) => void;
};

const OutputSchemaEditor: React.FC<OutputSchemaEditorProps> = ({
  initialValue,
  immutable: externalImmutable,
  onChange,
  onOpenJsonEditor,
  focusSchemaSection,
}) => {
  const { outputMode } = useAgentVersionConfigPanel();

  // JSON sidebar - use hook for open/close coordination
  const { open: openJsonEditor, isOpen: isJsonEditorOpen } =
    useSideDrawer("jsonSchema");
  const [isLocked, setIsLocked] = useState(false);
  const immutable = isLocked || externalImmutable;

  // Store the full UI schema (including $defs, _schema reference) in a ref
  // This is updated when user saves from JSON sidebar
  const fullSchemaRef = useRef<AgentSchemaUI | null>(
    convertAgentSchemaBEtoUI(initialValue),
  );

  // Track current properties for JSON sidebar sync
  const [currentProperties, setCurrentProperties] = useState<
    AgentSchemaPropertyUI[]
  >(fullSchemaRef.current?.properties ?? []);

  const isEmpty = currentProperties.length === 0;

  // Merges properties with stored schema ref (preserves $defs from JSON sidebar)
  const buildFullSchema = useCallback(
    (properties: AgentSchemaPropertyUI[]): AgentSchemaUI => {
      if (properties.length === 0) {
        fullSchemaRef.current = null;
        return EMPTY_UI_SCHEMA;
      }
      return {
        ...(fullSchemaRef.current ?? EMPTY_UI_SCHEMA),
        properties,
      };
    },
    [],
  );

  // Handle property changes from SchemaPropertiesEditor
  const handlePropertiesChange = useCallback(
    (properties: AgentSchemaPropertyUI[]) => {
      setCurrentProperties(properties);

      // In text mode, always send null
      if (outputMode === "text") {
        onChange(null);
        return;
      }

      // Build full schema with $defs preserved
      const fullSchema = buildFullSchema(properties);
      const backendSchema = convertAgentSchemaUIToBE(fullSchema);
      onChange(backendSchema);
    },
    [onChange, outputMode, buildFullSchema],
  );

  // Handle mode change
  const handleModeChange = (mode: OutputMode) => {
    focusSchemaSection({ outputMode: mode });
    if (mode === "text") {
      onChange(null);
    } else {
      const fullSchema = buildFullSchema(currentProperties);
      const backendSchema = convertAgentSchemaUIToBE(fullSchema);
      onChange(backendSchema);
    }
  };

  // Compute current schema JSON for sidebar
  const currentSchemaJson = useMemo(() => {
    const fullSchema = buildFullSchema(currentProperties);
    let backendSchema = convertAgentSchemaUIToBE(fullSchema);
    backendSchema = deepCopyWithoutKeys(backendSchema, [
      "_schema",
      "_schemaNode",
      "_isNew",
      "sensitive",
      "order",
    ]);
    return JSON.stringify(backendSchema, null, 2);
  }, [currentProperties, buildFullSchema]);

  // Dirty handler (for UI locking)
  const handleDirtyChange = useCallback((isDirty: boolean) => {
    setIsLocked(isDirty);
  }, []);

  // Handle sidebar close (onDiscard callback)
  const handleCloseJsonEditor = useCallback(() => {
    setIsLocked(false);
  }, []);

  // Save handler (JSON → UI sync)
  const handleSaveFromJsonEditor = useCallback(
    (rawSchema: string) => {
      try {
        const parsedSchema = JSON.parse(rawSchema) as AgentSchemaBE;
        const newUISchema = convertAgentSchemaBEtoUI(parsedSchema);
        if (newUISchema) {
          fullSchemaRef.current = newUISchema;
          setCurrentProperties(newUISchema.properties);
          onChange(parsedSchema);
        }
        setIsLocked(false);
        focusSchemaSection({
          tab: "output",
          outputMode: "structured",
          scrollDelay: 250,
        });
      } catch {
        // Error handling in sidebar component
      }
    },
    [onChange, focusSchemaSection],
  );

  // Sync sidebar config when sidebar is open (handles both initial open and property changes)
  useEffect(() => {
    if (isJsonEditorOpen) {
      onOpenJsonEditor({
        schema: currentSchemaJson,
        onSave: handleSaveFromJsonEditor,
        onDiscard: handleCloseJsonEditor,
        onDirtyChange: handleDirtyChange,
      });
    }
  }, [
    currentSchemaJson,
    isJsonEditorOpen,
    handleSaveFromJsonEditor,
    handleCloseJsonEditor,
    handleDirtyChange,
    onOpenJsonEditor,
  ]);

  return (
    <div>
      {/* Structured/Text Toggle */}
      <div className="mb-4 flex items-center justify-between">
        <PillToggle value={outputMode} onChange={handleModeChange}>
          <PillToggle.Button value="structured">
            Structured output
          </PillToggle.Button>
          <PillToggle.Button value="text">Text output</PillToggle.Button>
        </PillToggle>
        {outputMode === "structured" && !isEmpty && (
          <Button
            dataLoc="open-json-schema-editor"
            disabled={immutable}
            size="sm"
            variant="secondary"
            onClick={openJsonEditor}
          >
            JSON view
          </Button>
        )}
      </div>

      {/* Content based on mode */}
      {outputMode === "text" && (
        <div className="text-gray-500 font-inter-normal-12px">
          Get the agent's response as unstructured text. Choose this when you
          need more flexible, human-readable outputs.
        </div>
      )}

      {outputMode === "structured" && (
        <>
          <div className="mb-4 text-gray-500 font-inter-normal-12px">
            Structure the agent's output as a JSON object to easily process and
            use the response. This ensures consistent, parsable outputs.
          </div>

          {isEmpty ? (
            <JsonSchemaEmptyState
              immutable={immutable}
              onAddField={() => {
                const newProperty = getAgentNewProperty(currentProperties);
                handlePropertiesChange([...currentProperties, newProperty]);
              }}
              onOpenRawJsonView={openJsonEditor}
            />
          ) : (
            <SchemaPropertiesEditor
              dataLocPrefix="output"
              immutable={immutable}
              initialProperties={currentProperties}
              onChange={handlePropertiesChange}
            />
          )}
        </>
      )}
    </div>
  );
};

// ============================================================================
// Schema Field Header
// ============================================================================

const SchemaFieldHeader: React.FC = () => (
  <div className="mb-2 flex items-center gap-2 text-gray-500 font-inter-normal-12px">
    <div className="w-80 pl-1">Name</div>
    <div className="w-40 pl-1">Type</div>
    <div className="w-16" />
  </div>
);

// ============================================================================
// Enum Values Editor Component
// ============================================================================

type EnumValuesEditorProps = {
  enumValues: EnumValueObject[];
  immutable: boolean;
  onChange: (newValues: EnumValueObject[]) => void;
};

const EnumValuesEditor: React.FC<EnumValuesEditorProps> = ({
  enumValues,
  immutable,
  onChange,
}) => {
  const [newEnumValue, setNewEnumValue] = useState("");

  const handleRemoveEnumValue = (enumIndex: number) => {
    onChange(enumValues.filter((_, i) => i !== enumIndex));
  };

  const handleAddEnumValue = () => {
    const trimmed = newEnumValue.trim();
    if (trimmed) {
      onChange([...enumValues, { value: trimmed }]);
      setNewEnumValue("");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddEnumValue();
    }
  };

  return (
    <div className="ml-4 mt-2 flex flex-wrap items-center gap-2">
      {enumValues.map((enumItem, enumIndex) => (
        <Pill key={enumIndex} size="sm" variant="gray">
          <Pill.Text>{enumItem.value}</Pill.Text>
          {!immutable && (
            <Icon
              color="text-gray-400 hover:text-gray-600"
              icon={faTimes}
              size="xs"
              onClick={() => handleRemoveEnumValue(enumIndex)}
            />
          )}
        </Pill>
      ))}
      {!immutable && (
        <div className="flex items-center gap-1">
          <input
            className="w-20 rounded border border-gray-200 px-2 py-1 text-gray-800 font-inter-normal-12px focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            placeholder="value"
            value={newEnumValue}
            onChange={(e) => setNewEnumValue(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <Button
            iconLeft={faPlus}
            size="sm"
            variant="secondary"
            onClick={handleAddEnumValue}
          >
            Add
          </Button>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Schema Field Row Component
// ============================================================================

type SchemaFieldRowProps = {
  index: number;
  immutable: boolean;
  onDelete: () => void;
};

const SchemaFieldRow: React.FC<SchemaFieldRowProps> = ({
  index,
  immutable,
  onDelete,
}) => {
  const hoverProps = useOpenOnHover<HTMLDivElement, HTMLDivElement>(100);
  const {
    control,
    formState: { errors },
  } = useFormContext<SchemaPropertiesFormValues>();

  const fieldError = errors.properties?.[index]?.fieldName;

  // Watch the property to build schema preview
  const propertyUI = useWatch({
    control,
    name: `properties.${index}`,
  }) as AgentSchemaPropertyUI;

  // Build schema node for preview
  const schemaNode = PropertyConverter.uiToBEProperty(
    propertyUI,
    SchemaOptions.Output,
  ) as JsonSchemaNode;

  const fullSchema = propertyUI._schema as JsonSchema;

  // Build preview schema with field name as property key
  const treePreviewSchema = useMemo(
    () => ({
      ...(fullSchema ?? {}),
      properties: {
        [propertyUI.fieldName]: schemaNode,
      },
    }),
    [fullSchema, propertyUI.fieldName, schemaNode],
  );

  // Determine if preview should be shown
  const showPreview =
    schemaNode &&
    !isPrimitive(schemaNode) &&
    !isArrayOfPrimitive(schemaNode) &&
    !isUnionOfPrimitivesOnly(schemaNode) &&
    !(isObject(schemaNode) && !isObjectWithProperties(schemaNode));

  return (
    <div className="mb-3">
      {/* Main row */}
      <div className="flex items-center gap-2">
        <div className="w-80">
          <Controller
            control={control}
            name={`properties.${index}.fieldName`}
            render={({ field }) => (
              <Input
                disabled={immutable}
                errored={!!fieldError}
                placeholder="field_name"
                value={field.value}
                fullWidth
                onChange={field.onChange}
              />
            )}
            rules={{
              validate: {
                notEmpty: (value) =>
                  value?.trim() !== "" || "Field name cannot be empty",
                unique: (value, formValues) => {
                  const allFieldNames = formValues.properties.map(
                    (p) => p.fieldName,
                  );
                  const duplicateCount = allFieldNames.filter(
                    (name) => name === value,
                  ).length;
                  return duplicateCount <= 1 || "Field name must be unique";
                },
              },
            }}
          />
        </div>
        <div className="w-40">
          <Controller
            control={control}
            name={`properties.${index}`}
            render={({ field }) => {
              const primaryType = field.value.type?.[0] ?? "string";
              const currentNullable = field.value.type?.[1] ?? false;
              return (
                <FixedSimpleDropdown
                  buttonDataLoc={`schema-type-${index}`}
                  disabled={immutable}
                  elements={AGENT_SCHEMA_TYPE_OPTIONS}
                  placeholder="Select type"
                  selectedKey={primaryType}
                  onSelect={(key) => {
                    field.onChange({
                      ...field.value,
                      type: [key, currentNullable],
                      enum: key === "enum" ? [] : undefined,
                    });
                  }}
                />
              );
            }}
          />
        </div>
        <div className="flex w-16 items-center justify-start gap-1">
          {/* Settings Hover Popup */}
          <div className="relative">
            <div
              onMouseEnter={hoverProps.onMouseEnter}
              onMouseLeave={hoverProps.onMouseLeave}
            >
              <Icon
                color={twJoin(
                  !immutable && "cursor-pointer",
                  "text-gray-500 hover:text-gray-700",
                )}
                dataLoc={`schema-field-advanced-trigger-${index}`}
                icon={faCog}
                size="xs"
              />
            </div>
            {hoverProps.isOpen && (
              <div
                className="absolute -right-3 z-50 w-[9.5rem] rounded-lg bg-white px-3 py-4 text-gray-800 shadow-lg font-inter-normal-13px"
                data-loc={`schema-field-advanced-popup-${index}`}
                onMouseEnter={hoverProps.onMouseEnter}
                onMouseLeave={hoverProps.onMouseLeave}
              >
                <Controller
                  control={control}
                  name={`properties.${index}.required`}
                  render={({ field }) => (
                    <div className="mb-4 flex items-center justify-between">
                      <span>Required</span>
                      <Switch
                        dataLoc={`schema-field-required-${index}`}
                        disabled={immutable}
                        enabled={field.value}
                        onChange={field.onChange}
                      />
                    </div>
                  )}
                />
                <Controller
                  control={control}
                  name={`properties.${index}.type`}
                  render={({ field }) => {
                    const [primaryType, nullable] = field.value ?? [
                      "string",
                      false,
                    ];
                    const isNullable = nullable === "null";
                    return (
                      <div className="flex items-center justify-between">
                        <span>Nullable</span>
                        <Switch
                          dataLoc={`schema-field-nullable-${index}`}
                          disabled={immutable}
                          enabled={isNullable}
                          onChange={(checked) => {
                            field.onChange([
                              primaryType,
                              checked ? "null" : false,
                            ]);
                          }}
                        />
                      </div>
                    );
                  }}
                />
              </div>
            )}
          </div>
          {/* Delete button */}
          {!immutable && (
            <Icon
              color="text-gray-500 hover:text-red-600"
              dataLoc={`delete-field-${index}`}
              icon={faTrashAlt}
              size="xs"
              onClick={onDelete}
            />
          )}
        </div>
      </div>

      {/* Field-level error hints */}
      {fieldError && <ErrorHint margin="none">{fieldError.message}</ErrorHint>}

      {/* Description field */}
      <Controller
        control={control}
        name={`properties.${index}.description`}
        render={({ field }) => (
          <div className="mt-2">
            <textarea
              className="w-full rounded-md border border-gray-200 px-3 py-2 text-gray-800 font-inter-normal-12px placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-50 disabled:text-gray-500"
              disabled={immutable}
              placeholder="Tell the agent about this field..."
              rows={2}
              value={field.value || ""}
              onChange={field.onChange}
            />
          </div>
        )}
      />

      {/* Enum values editor - shown when type is "enum" */}
      <Controller
        control={control}
        name={`properties.${index}`}
        render={({ field }) => {
          const primaryType = field.value.type?.[0];
          if (primaryType !== "enum") return <></>;

          const enumValues = (field.value.enum ?? []) as EnumValueObject[];

          return (
            <EnumValuesEditor
              enumValues={enumValues}
              immutable={immutable}
              onChange={(newValues) => {
                field.onChange({
                  ...field.value,
                  enum: newValues,
                });
              }}
            />
          );
        }}
      />

      {/* JSON Schema Preview for nested/composite types */}
      {showPreview && (
        <div className="ml-1 mt-2">
          <JSONSchemaRenderer
            displayOptions={{
              showTypeLabels: true,
              showPrimitiveTypeLabels: true,
              showDataPaths: false,
              maxDepth: 6,
              showRequired: false,
              customLabelAndTypeRenderer: createTitleOnlyHeaderRenderer(),
              autoExpandDepth: 3,
            }}
            schema={treePreviewSchema}
          />
        </div>
      )}
    </div>
  );
};
